import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  ListGroup,
  Badge,
  Button,
} from "react-bootstrap";
import HallOfFame from "./HallOfFame";
import RankChangesPanel from "./RankChangesPanel";
import { getLevelModifierClass } from "../utils/levelColors";

const API_URL = process.env.REACT_APP_API_URL;

const emptyStats = {
  latestSeasonName: "Hà Đông Super League",
  totalSeasons: 0,
  totalTeams: 0,
  totalPlayers: 0,
};

const levelMap = {
  8: "H",
  7: "G",
  1: "A",
  2: "B",
  3: "B",
  4: "C",
  5: "B",
  6: "A",
};

const getSerieLabel = (levelId, levelName) => {
  const normalizedLevelName = String(levelName || "").trim();
  if (Number(levelId) === 2 || normalizedLevelName === "Serie E") return "Serie B";
  if (Number(levelId) === 4) return "Serie C";
  if (normalizedLevelName) return normalizedLevelName;
  return levelMap[levelId] ? `Serie ${levelMap[levelId]}` : `Serie ${levelId || "khác"}`;
};

const getLevelNameFromIds = (levelIds) => {
  if (!levelIds || levelIds.length === 0) return "N/A";
  return levelIds.map((id) => levelMap[id] || `ID ${id}`).join(", ");
};

const getYearFromDate = (date) => {
  if (!date) return null;
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getFullYear();
};

const safeGet = async (url, fallback) => {
  try {
    const res = await axios.get(url);
    return res.data ?? fallback;
  } catch {
    return fallback;
  }
};

const isRealName = (value) => value && value !== "N/A" && value !== "Không rõ";

const emptyPendingRound = {
  roundId: null,
  schedules: [],
};

const hasScheduleResult = (schedule) =>
  schedule.home_result !== null &&
  schedule.home_result !== undefined &&
  schedule.guest_result !== null &&
  schedule.guest_result !== undefined;

const isOpenSchedule = (schedule) =>
  Number(schedule?.status) === 0;

const getRoundId = (schedule) => Number(schedule?.round_id || 0);

const getPendingRound = (schedules) => {
  const openSchedules = (schedules || []).filter(isOpenSchedule);

  if (openSchedules.length === 0) return emptyPendingRound;

  const roundId = openSchedules.reduce((currentRound, schedule) => {
    const nextRound = getRoundId(schedule);
    if (!currentRound || nextRound < currentRound) return nextRound;
    return currentRound;
  }, null);

  return {
    roundId,
    schedules: openSchedules
      .filter((schedule) => getRoundId(schedule) === roundId)
      .sort(
        (a, b) =>
          new Date(a.play_date) - new Date(b.play_date) ||
          Number(a.level_team_id || 0) - Number(b.level_team_id || 0) ||
          Number(a.table_id || 0) - Number(b.table_id || 0) ||
          Number(a.id || 0) - Number(b.id || 0),
      ),
  };
};

const Dashboard = () => {
  const [stats, setStats] = useState(emptyStats);
  const [loading, setLoading] = useState(true);
  const [loadingYearData, setLoadingYearData] = useState(false);
  const [error, setError] = useState(null);
  const [championTeam, setChampionTeam] = useState([]);
  const [championStats, setChampionStats] = useState({});
  const [selectedYear, setSelectedYear] = useState("");
  const [availableYears, setAvailableYears] = useState([]);
  const [historicYears, setHistoricYears] = useState([]);
  const [individualTournaments, setIndividualTournaments] = useState([]);
  const [mostMatchesPlayer, setMostMatchesPlayer] = useState(null);
  const [mostWinsPlayer, setMostWinsPlayer] = useState(null);
  const [highestPointsPlayer, setHighestPointsPlayer] = useState(null);
  const [latestNews, setLatestNews] = useState([]);
  const [dataCache, setDataCache] = useState({});
  const [pendingRound, setPendingRound] = useState(emptyPendingRound);
  const [selectedSeasonId, setSelectedSeasonId] = useState(null);

  const handlePrevYear = () => {
    const currentIndex = availableYears.indexOf(parseInt(selectedYear));
    if (currentIndex < availableYears.length - 1) {
      setSelectedYear(availableYears[currentIndex + 1].toString());
    }
  };

  const handleNextYear = () => {
    const currentIndex = availableYears.indexOf(parseInt(selectedYear));
    if (currentIndex > 0) {
      setSelectedYear(availableYears[currentIndex - 1].toString());
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchYearsList = async () => {
      try {
        setLoading(true);

        const [seasonsRes, historicDataRes] = await Promise.all([
          axios.get(`${API_URL}/seasons`),
          fetch("/data.json").then((res) => res.json()).catch(() => ({})),
        ]);

        if (!isMounted) return;

        const allSeasons = seasonsRes.data || [];
        const apiYears = [
          ...new Set(
            allSeasons
              .map((season) => getYearFromDate(season.date_start))
              .filter(Boolean),
          ),
        ];
        const jsonYears = Object.keys(historicDataRes).map((year) => parseInt(year));
        const years = [...new Set([...apiYears, ...jsonYears])].sort((a, b) => b - a);

        setHistoricYears(jsonYears);
        setAvailableYears(years);
        setStats((prev) => ({
          ...prev,
          totalSeasons: allSeasons.length,
        }));

        if (years.length > 0) {
          setSelectedYear(years[0].toString());
        }
      } catch (err) {
        console.error("Error fetching years list:", err);
        if (isMounted) setError("Không thể tải danh sách năm.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchYearsList();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchLatestNews = async () => {
      const news = await safeGet(`${API_URL}/news`, []);
      if (isMounted) setLatestNews((news || []).slice(0, 3));
    };

    fetchLatestNews();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedYear) return;

    let isMounted = true;
    const isLatestSelectedYear = selectedYear === availableYears[0]?.toString();

    const applyYearData = (yearData) => {
      setStats((prev) => ({ ...prev, ...yearData.stats }));
      setChampionTeam(yearData.championTeam);
      setChampionStats(yearData.championStats);
      setIndividualTournaments(yearData.individualTournaments);
      setMostMatchesPlayer(yearData.mostMatchesPlayer);
      setMostWinsPlayer(yearData.mostWinsPlayer);
      setHighestPointsPlayer(yearData.highestPointsPlayer);
      setPendingRound(yearData.pendingRound || emptyPendingRound);
      setSelectedSeasonId(yearData.seasonId ?? null);
    };

    const fetchYearData = async () => {
      try {
        setLoadingYearData(true);
        setError(null);

        if (dataCache[selectedYear]) {
          applyYearData(dataCache[selectedYear]);
          setLoadingYearData(false);
          return;
        }

        const [allSeasons, allTournaments] = await Promise.all([
          safeGet(`${API_URL}/seasons`, []),
          safeGet(`${API_URL}/tournaments/all-json`, []),
        ]);

        if (!isMounted) return;

        const seasonsInSelectedYear = (allSeasons || [])
          .filter((season) => getYearFromDate(season.date_start)?.toString() === selectedYear)
          .sort((a, b) => {
            const dateDiff = new Date(b.date_start) - new Date(a.date_start);
            return dateDiff || Number(b.id || 0) - Number(a.id || 0);
          });

        const latestSeasonInSelectedYear = seasonsInSelectedYear[0] || null;
        const tournamentsInSelectedYear = (allTournaments || []).filter((tournament) => {
          const year =
            getYearFromDate(tournament.created_at) ||
            getYearFromDate(tournament.start_date);
          return year?.toString() === selectedYear;
        });

        if (!latestSeasonInSelectedYear) {
          const yearData = {
            stats: {
              latestSeasonName: `Hà Đông Super League ${selectedYear}`,
              totalTeams: 0,
              totalPlayers: 0,
            },
            championTeam: [],
            championStats: {},
            individualTournaments: tournamentsInSelectedYear,
            mostMatchesPlayer: null,
            mostWinsPlayer: null,
            highestPointsPlayer: null,
            pendingRound: emptyPendingRound,
            seasonId: null,
          };

          setDataCache((prev) => ({ ...prev, [selectedYear]: yearData }));
          applyYearData(yearData);
          return;
        }

        const seasonId = latestSeasonInSelectedYear.id;
        const [
          teamsCount,
          playersCount,
          mostMatches,
          mostWins,
          highestPoints,
          champions,
          groupSchedules,
        ] = await Promise.all([
          safeGet(`${API_URL}/teams/count?season_id=${seasonId}`, { total: 0 }),
          safeGet(`${API_URL}/playerspublic/count?season_id=${seasonId}`, { total: 0 }),
          safeGet(`${API_URL}/playerspublic/most-matches?season_id=${seasonId}`, null),
          safeGet(`${API_URL}/playerspublic/most-wins?season_id=${seasonId}`, null),
          safeGet(`${API_URL}/playerspublic/highest-points?season_id=${seasonId}`, null),
          safeGet(`${API_URL}/seasons/${seasonId}/champion`, []),
          isLatestSelectedYear
            ? safeGet(`${API_URL}/schedules?season_id=${seasonId}&round_type=1`, [])
            : Promise.resolve([]),
        ]);

        if (!isMounted) return;

        const statsPairs = await Promise.all(
          (champions || []).map(async (champion) => {
            if (!champion.champion_team_id) return {};
            const teamStats = await safeGet(
              `${API_URL}/teams/${champion.champion_team_id}/season-stats?seasonId=${seasonId}`,
              null,
            );
            return teamStats ? { [champion.level_team_id]: teamStats } : {};
          }),
        );

        if (!isMounted) return;

        const hasSeasonChampion = (champions || []).some((champion) =>
          isRealName(champion.champion_team_name),
        );
        const pendingRound =
          isLatestSelectedYear && !hasSeasonChampion
            ? getPendingRound(groupSchedules)
            : emptyPendingRound;

        const yearData = {
          stats: {
            latestSeasonName: latestSeasonInSelectedYear.name,
            totalTeams: teamsCount.total || 0,
            totalPlayers: playersCount.total || 0,
          },
          championTeam: champions || [],
          championStats: Object.assign({}, ...statsPairs),
          individualTournaments: tournamentsInSelectedYear,
          mostMatchesPlayer: mostMatches,
          mostWinsPlayer: mostWins,
          highestPointsPlayer: highestPoints,
          pendingRound,
          seasonId,
        };

        setDataCache((prev) => ({ ...prev, [selectedYear]: yearData }));
        applyYearData(yearData);
      } catch (err) {
        console.error("Error fetching year data:", err);
        if (isMounted) setError("Không thể tải dữ liệu của năm này.");
      } finally {
        if (isMounted) setLoadingYearData(false);
      }
    };

    fetchYearData();

    return () => {
      isMounted = false;
    };
  }, [selectedYear, availableYears, dataCache]);

  const hasHistoricDataForYear = useMemo(
    () => historicYears.includes(parseInt(selectedYear)),
    [historicYears, selectedYear],
  );

  const hasChampionData = useMemo(
    () =>
      championTeam.some((champion) =>
        [
          champion.champion_team_name,
          champion.second_team_name,
          champion.third_team_name,
        ].some(isRealName),
      ),
    [championTeam],
  );

  const playerHighlights = useMemo(
    () => [
      {
        label: "Thi đấu nhiều nhất",
        player: mostMatchesPlayer,
        value: mostMatchesPlayer?.total_matches,
        suffix: "trận",
      },
      {
        label: "Thắng nhiều nhất",
        player: mostWinsPlayer,
        value: mostWinsPlayer?.total_wins,
        suffix: "chiến thắng",
      },
      {
        label: "Điểm cao nhất",
        player: highestPointsPlayer,
        value: highestPointsPlayer?.total_points,
        suffix: "điểm",
      },
    ],
    [mostMatchesPlayer, mostWinsPlayer, highestPointsPlayer],
  );

  const hasPlayerHighlights = playerHighlights.some(
    (item) => item.player?.name && item.value !== null && item.value !== undefined,
  );

  const pendingRoundGroups = useMemo(() => {
    const groups = new Map();

    pendingRound.schedules.forEach((match) => {
      const serieLabel = getSerieLabel(match.level_team_id, match.level_team_name);
      const key = `${match.level_team_id || serieLabel}`;

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          name: serieLabel,
          levelId: Number(match.level_team_id || 0),
          matches: [],
        });
      }

      groups.get(key).matches.push(match);
    });

    return Array.from(groups.values()).sort(
      (a, b) => a.levelId - b.levelId || a.name.localeCompare(b.name, "vi"),
    );
  }, [pendingRound]);

  const isLatestYearSelected = selectedYear === availableYears[0]?.toString();
  const isCurrentSeasonView = isLatestYearSelected && !hasChampionData;
  const isPreseasonView =
    isCurrentSeasonView && !hasHistoricDataForYear && !hasPlayerHighlights;
  const showPendingRoundSchedules =
    isCurrentSeasonView && pendingRoundGroups.length > 0 && pendingRound.roundId;

  if (loading) {
    return (
      <div className="client-home-loading">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p>Đang tải danh sách năm...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="client-home-alert">
        {error}
      </Alert>
    );
  }

  return (
    <div className="client-home py-4">
      <section className="client-home-hero animate-fade">
        <div className="client-home-hero-main">
          <p className="client-kicker">Hà Đông Super League</p>
          <h1>
            {loadingYearData ? (
              <Spinner animation="border" size="sm" />
            ) : (
              stats.latestSeasonName
            )}
          </h1>
          <p className="client-home-hero-copy">
            Trung tâm theo dõi mùa giải, bảng xếp hạng, lịch thi đấu, đội bóng,
            vận động viên và các giải cá nhân.
          </p>
          <div className="client-home-actions">
            <Button as={Link} to="/match-results" className="client-primary-btn">
              Lịch & kết quả
            </Button>
            <Button as={Link} to="/league-details" className="client-secondary-btn">
              Bảng xếp hạng
            </Button>
          </div>
        </div>

        <div className="client-year-card">
          <span>Năm đang xem</span>
          <div className="client-year-switcher">
            <button
              type="button"
              onClick={handlePrevYear}
              disabled={
                availableYears.indexOf(parseInt(selectedYear)) ===
                availableYears.length - 1
              }
              aria-label="Năm trước"
            >
              ‹
            </button>
            <strong>{selectedYear}</strong>
            <button
              type="button"
              onClick={handleNextYear}
              disabled={availableYears.indexOf(parseInt(selectedYear)) === 0}
              aria-label="Năm sau"
            >
              ›
            </button>
          </div>
          <small>{availableYears.length} năm dữ liệu</small>
        </div>
      </section>

      {loadingYearData ? (
        <div className="client-home-loading client-home-loading-panel">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Đang tải dữ liệu năm {selectedYear}...</p>
        </div>
      ) : (
        <>
          <section className="client-stat-grid">
            <article className="client-stat-card">
              <span>Năm</span>
              <strong>{selectedYear}</strong>
              <small>Mùa đang chọn</small>
            </article>
            <article className="client-stat-card">
              <span>Đội bóng</span>
              <strong>{stats.totalTeams}</strong>
              <small>Đội đã ghi nhận</small>
            </article>
            <article className="client-stat-card">
              <span>Vận động viên</span>
              <strong>{stats.totalPlayers}</strong>
              <small>Hồ sơ trong mùa</small>
            </article>
            <article className="client-stat-card">
              <span>Giải cá nhân</span>
              <strong>{individualTournaments.length}</strong>
              <small>Giải trong năm</small>
            </article>
          </section>

          {isCurrentSeasonView && <RankChangesPanel compact />}

          {!isCurrentSeasonView && selectedSeasonId && (
            <RankChangesPanel compact seasonId={selectedSeasonId} showAllInSeason />
          )}

          {showPendingRoundSchedules && (
            <section className="client-section">
              <div className="client-section-head">
                <div>
                  <p className="client-kicker">Lịch thi đấu</p>
                  <h2>Vòng {pendingRound.roundId} đang diễn ra</h2>
                </div>
                <Badge className="client-soft-badge">
                  {pendingRound.schedules.length} trận
                </Badge>
                <Button as={Link} to="/match-results" className="client-secondary-btn">
                  Xem tất cả
                </Button>
              </div>
              <div className="client-live-round-list">
                {pendingRoundGroups.map((group) => (
                  <article
                    className={`client-live-serie ${getLevelModifierClass(group.name)}`}
                    key={group.key}
                  >
                    <div className="client-live-serie-head">
                      <strong>{group.name}</strong>
                      <span>{group.matches.length} trận</span>
                    </div>
                    <div className="client-live-match-list">
                      {group.matches.map((match) => (
                        <div className="client-live-match-row" key={match.id}>
                          <div className="client-live-match-meta">
                            <span className="client-live-date">
                              {match.play_date
                                ? new Date(match.play_date).toLocaleDateString("vi-VN", {
                                  day: "2-digit",
                                  month: "2-digit",
                                })
                                : "--/--"}
                            </span>
                            <span className="client-live-table">
                              {match.table_name || "Vòng bảng"}
                            </span>
                          </div>
                          <div className="client-live-match-teams">
                            <Link to={`/teams/${match.home}`} className="client-live-team home">
                              {match.home_name}
                            </Link>
                            <span className="client-live-vs">
                              {hasScheduleResult(match)
                                ? `${match.home_result} - ${match.guest_result}`
                                : "VS"}
                            </span>
                            <Link to={`/teams/${match.guest}`} className="client-live-team">
                              {match.guest_name}
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {isCurrentSeasonView ? (
            <section className="season-readiness-card">
              <div>
                <p className="client-kicker">Mùa giải hiện tại</p>
                <h2>
                  {isPreseasonView
                    ? "Đang khởi động dữ liệu thi đấu"
                    : "Mùa giải đang diễn ra"}
                </h2>
                <p>
                  Trang tổng quan ưu tiên vòng đấu cần nhập liệu, danh sách đội,
                  vận động viên, bảng xếp hạng và các cập nhật mới nhất của mùa.
                </p>
              </div>
              <div className="readiness-grid">
                <Link to="/teams" className="readiness-item">
                  <span>Đội bóng</span>
                  <strong>{stats.totalTeams || "Xem danh sách"}</strong>
                  <small>Kiểm tra lực lượng từng đội</small>
                </Link>
                <Link to="/players" className="readiness-item">
                  <span>VĐV</span>
                  <strong>{stats.totalPlayers || "Tra cứu"}</strong>
                  <small>Tìm hồ sơ, trình và lịch sử</small>
                </Link>
                <Link to="/match-results" className="readiness-item">
                  <span>Lịch đấu</span>
                  <strong>
                    {pendingRound.schedules.length > 0
                      ? `${pendingRound.schedules.length} trận vòng ${pendingRound.roundId}`
                      : "Sắp cập nhật"}
                  </strong>
                  <small>Theo dõi lịch khi BTC công bố</small>
                </Link>
                <Link to="/league-details" className="readiness-item">
                  <span>BXH</span>
                  <strong>Chờ điểm</strong>
                  <small>Tự cập nhật sau khi có kết quả</small>
                </Link>
              </div>
            </section>
          ) : (
            <section className="client-section">
              <div className="client-section-head">
                <div>
                  <p className="client-kicker">Vinh danh</p>
                  <h2>Thành tích mùa {selectedYear}</h2>
                </div>
              </div>
              <HallOfFame
                selectedYear={selectedYear}
                championTeam={championTeam}
                championStats={championStats}
                loadingYearData={loadingYearData}
              />
            </section>
          )}

          <section className="client-section">
            <div className="client-section-head">
              <div>
                <p className="client-kicker">Cá nhân</p>
                <h2>Vận động viên tiêu biểu {selectedYear}</h2>
              </div>
              <Button as={Link} to="/players" className="client-secondary-btn">
                Xem VĐV
              </Button>
            </div>
            {hasPlayerHighlights ? (
              <Row className="g-3">
                {playerHighlights.map((item) => (
                  <Col md={4} xs={12} key={item.label}>
                    <Card className="h-100 client-highlight-card">
                      <Card.Body>
                        <span>{item.label}</span>
                        <h3>{item.player?.name}</h3>
                        <strong>
                          {item.value} <small>{item.suffix}</small>
                        </strong>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <div className="client-empty-panel">
                <strong>Chưa có thống kê cá nhân cho năm {selectedYear}.</strong>
                <span>Các chỉ số sẽ xuất hiện sau khi trận đấu được nhập và tính điểm.</span>
              </div>
            )}
          </section>

          <section className="client-section">
            <div className="client-section-head">
              <div>
                <p className="client-kicker">Giải cá nhân</p>
                <h2>Đơn, đôi và đăng ký</h2>
              </div>
              <Button as={Link} to="/tournaments" className="client-secondary-btn">
                Xem tất cả
              </Button>
            </div>

            {individualTournaments.length > 0 ? (
              <Row className="g-3">
                {individualTournaments.slice(0, 4).map((tournament) => {
                  const registeredCount = tournament.registrations?.length || 0;
                  const playerLimit = tournament.player_limit || 0;
                  const isFull = playerLimit > 0 && registeredCount >= playerLimit;
                  const progress = playerLimit
                    ? Math.min(100, (registeredCount / playerLimit) * 100)
                    : 0;

                  return (
                    <Col md={6} lg={3} key={tournament.id}>
                      <Card className="h-100 client-tournament-card">
                        <Card.Body>
                          <div>
                            <Badge className="client-soft-badge">
                              {tournament.type === "single" ? "Đơn" : "Đôi"}
                            </Badge>
                            <h3>{tournament.name}</h3>
                          </div>

                          <ListGroup variant="flush" className="client-tournament-list">
                            <ListGroup.Item>
                              <span>Cấp độ</span>
                              <strong>{getLevelNameFromIds(tournament.level_ids)}</strong>
                            </ListGroup.Item>
                            {tournament.start_date && (
                              <ListGroup.Item>
                                <span>Thời gian</span>
                                <strong>
                                  {new Date(tournament.start_date).toLocaleDateString("vi-VN")}
                                </strong>
                              </ListGroup.Item>
                            )}
                            {tournament.location && (
                              <ListGroup.Item>
                                <span>Địa điểm</span>
                                <strong>{tournament.location}</strong>
                              </ListGroup.Item>
                            )}
                          </ListGroup>

                          <div className="tournament-progress">
                            <div>
                              <span>Đã đăng ký</span>
                              <strong>
                                {registeredCount} / {playerLimit || "?"}
                              </strong>
                            </div>
                            <div className="client-progress-track">
                              <div style={{ width: `${progress}%` }} />
                            </div>
                          </div>

                          {tournament.status === "draw_generated" ? (
                            <Button
                              as={Link}
                              to={`/tournament-schedule/${tournament.id}`}
                              className="client-primary-btn w-100"
                            >
                              Lịch thi đấu
                            </Button>
                          ) : (
                            <Button
                              as={Link}
                              to={`/register-tournament/${tournament.id}`}
                              className="client-primary-btn w-100"
                              disabled={isFull}
                            >
                              {isFull ? "Đã đóng" : "Đăng ký"}
                            </Button>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            ) : (
              <div className="client-empty-panel">
                <strong>Chưa có giải cá nhân trong năm {selectedYear}.</strong>
                <span>Khi có giải mới, người dùng có thể xem thông tin và đăng ký tại đây.</span>
              </div>
            )}
          </section>

          {latestNews.length > 0 && (
            <section className="client-section">
              <div className="client-section-head">
                <div>
                  <p className="client-kicker">Tin tức</p>
                  <h2>Cập nhật mới nhất</h2>
                </div>
                <Button as={Link} to="/news" className="client-secondary-btn">
                  Xem tin tức
                </Button>
              </div>
              <Row className="g-3">
                {latestNews.map((article) => (
                  <Col md={4} xs={12} key={article.id}>
                    <Link to={`/news/${article.id}`} className="client-news-card">
                      <strong>{article.title}</strong>
                      <span>
                        {article.created_at
                          ? new Date(article.created_at).toLocaleDateString("vi-VN")
                          : "Tin mới"}
                      </span>
                    </Link>
                  </Col>
                ))}
              </Row>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default Dashboard;
